import { bunja } from 'bunja';
import { Atom, atom, type Getter } from 'jotai';
import { withAtomEffect } from 'jotai-effect';

import { JotaiStoreScope } from '@flipster/utils/bunja-jotai';

import { symbolBunja } from './base';
import { OrderState, convert, formBunja, getKindValue } from './form';
import { resolveOrderPrice } from './logic/derivation/order-price';

export const realtimePriceBunja = bunja(() => {
  const { askPriceAtom: pAskPriceAtom, bidPriceAtom: pBidPriceAtom } = bunja.use(symbolBunja);
  const { referencePriceAtom, triggerPriceAtom, limitPriceAtom, priceTypeAtom, isBboAtom, triggerExecutionTypeAtom } =
    bunja.use(formBunja);
  /**
   * Trigger Limit / Limit 주문은 limit price(BBO 시 ticker bid/ask) 기반, Trigger Market 등은 trigger price 기반.
   * trigger price ≠ order price.
   */
  const resolveParams = (get: Getter, fallbackPrice: string) => ({
    priceType: get(priceTypeAtom),
    triggerExecutionType: get(triggerExecutionTypeAtom),
    isBbo: get(isBboAtom),
    limitPrice: get(limitPriceAtom),
    triggerPrice: get(triggerPriceAtom),
    fallbackPrice,
  });
  const askPriceAtom = atom<string>((get) => resolveOrderPrice(resolveParams(get, get(pAskPriceAtom))));
  const bidPriceAtom = atom<string>((get) => resolveOrderPrice(resolveParams(get, get(pBidPriceAtom))));
  const priceAtom = atom((get) => resolveOrderPrice(resolveParams(get, get(referencePriceAtom))));

  return { askPriceAtom, bidPriceAtom, priceAtom };
});
realtimePriceBunja.debugLabel = 'realtimePriceBunja';

export const throttledPriceBunja = bunja(() => {
  const { askPriceAtom: pAskPriceAtom, bidPriceAtom: pBidPriceAtom } = bunja.use(symbolBunja);
  const { referencePriceAtom, triggerPriceAtom, limitPriceAtom, priceTypeAtom, isBboAtom, triggerExecutionTypeAtom } =
    bunja.use(formBunja);

  /** validation을 위해서 1초 throttle된 reference price 사용 */
  const throttledReferencePriceAtom = atom(
    (get) => get(throttledReferencePriceBaseAtom)?.value ?? get(referencePriceAtom),
  );
  const throttledReferencePriceBaseAtom = withAtomEffect(
    atom<{ value: string; timestamp: number } | undefined>(undefined),
    (get, set) => {
      get(referencePriceAtom); // does not depend on this if omitted;
      const prev = get(throttledReferencePriceBaseAtom);
      if (!prev || Date.now() - prev.timestamp >= 3000) update();
      const id = setTimeout(update, 1000);
      return () => clearTimeout(id);
      function update() {
        if (prev && prev.value === get.peek(referencePriceAtom)) return;
        set.recurse(throttledReferencePriceBaseAtom, { value: get(referencePriceAtom), timestamp: Date.now() });
      }
    },
  );

  const resolveParams = (get: Getter, fallbackPrice: string) => ({
    priceType: get(priceTypeAtom),
    triggerExecutionType: get(triggerExecutionTypeAtom),
    isBbo: get(isBboAtom),
    limitPrice: get(limitPriceAtom),
    triggerPrice: get(triggerPriceAtom),
    fallbackPrice,
  });
  const askPriceAtom = atom<string>((get) => resolveOrderPrice(resolveParams(get, get(pAskPriceAtom))));
  const bidPriceAtom = atom<string>((get) => resolveOrderPrice(resolveParams(get, get(pBidPriceAtom))));
  const priceAtom = atom((get) => resolveOrderPrice(resolveParams(get, get(throttledReferencePriceAtom))));
  return { throttledReferencePriceAtom, priceAtom, askPriceAtom, bidPriceAtom };
});
throttledPriceBunja.debugLabel = 'throttledPriceBunja';

export const realtimeStateBunja = bunja(() => {
  const store = bunja.use(JotaiStoreScope);
  const { symbolInfoAtom } = bunja.use(symbolBunja);
  const { orderStateAtom } = bunja.use(formBunja);
  const { priceAtom, askPriceAtom, bidPriceAtom } = bunja.use(realtimePriceBunja);
  return createStateAtoms({
    orderStateAtom,
    priceAtom,
    askPriceAtom,
    bidPriceAtom,
    tradeQtyPrecision: store.get(symbolInfoAtom).tradeQtyPrecision,
  });
});
realtimeStateBunja.debugLabel = 'realtimeStateBunja';

export const throttledStateBunja = bunja(() => {
  const store = bunja.use(JotaiStoreScope);
  const { symbolInfoAtom } = bunja.use(symbolBunja);
  const { orderStateAtom } = bunja.use(formBunja);
  const { priceAtom, askPriceAtom, bidPriceAtom } = bunja.use(throttledPriceBunja);
  return createStateAtoms({
    orderStateAtom,
    priceAtom,
    askPriceAtom,
    bidPriceAtom,
    tradeQtyPrecision: store.get(symbolInfoAtom).tradeQtyPrecision,
  });
});
throttledStateBunja.debugLabel = 'throttledStateBunja';

interface StateSources {
  orderStateAtom: Atom<OrderState>;
  priceAtom: Atom<string>;
  askPriceAtom: Atom<string>;
  bidPriceAtom: Atom<string>;
  tradeQtyPrecision: number | undefined;
}
function createStateAtoms({ orderStateAtom, priceAtom, askPriceAtom, bidPriceAtom, tradeQtyPrecision }: StateSources) {
  // quantity
  const quantityAtom = atom<string>((get) => {
    const state = get(orderStateAtom);
    const price = get(priceAtom);
    const quantityPrecision = tradeQtyPrecision;
    const stateTo = convert(state.leverage, price, { quantityPrecision })[state.kind](getKindValue(state));
    return stateTo.quantity.toString();
  });
  const askQuantityAtom = atom<string>((get) => {
    const state = get(orderStateAtom);
    if (state.kind === 'margin') {
      const askPrice = get(askPriceAtom);
      const quantityPrecision = tradeQtyPrecision;
      return convert(state.leverage, askPrice, { quantityPrecision }).margin(state.margin).quantity;
    }
    return get(quantityAtom);
  });
  const bidQuantityAtom = atom<string>((get) => {
    const state = get(orderStateAtom);
    if (state.kind === 'margin') {
      const bidPrice = get(bidPriceAtom);
      const quantityPrecision = tradeQtyPrecision;
      return convert(state.leverage, bidPrice, { quantityPrecision }).margin(state.margin).quantity;
    }
    return get(quantityAtom);
  });
  // margin
  const marginAtom = atom<string>((get) => {
    const state = get(orderStateAtom);
    const price = get(priceAtom);
    const stateTo = convert(state.leverage, price)[state.kind](getKindValue(state));
    return stateTo.margin.toString();
  });
  const askMarginAtom = atom<string>((get) => {
    const state = get(orderStateAtom);
    if (state.kind === 'quantity') {
      const askPrice = get(askPriceAtom);
      return convert(state.leverage, askPrice).quantity(state.quantity).margin;
    }
    return get(marginAtom);
  });
  const bidMarginAtom = atom<string>((get) => {
    const state = get(orderStateAtom);
    if (state.kind === 'quantity') {
      const bidPrice = get(bidPriceAtom);
      return convert(state.leverage, bidPrice).quantity(state.quantity).margin;
    }
    return get(marginAtom);
  });
  // amount
  const amountAtom = atom<string>((get) => {
    const state = get(orderStateAtom);
    const price = get(priceAtom);
    const stateTo = convert(state.leverage, price)[state.kind](getKindValue(state));
    return stateTo.amount.toString();
  });
  const askAmountAtom = atom<string>((get) => {
    const state = get(orderStateAtom);
    if (state.kind === 'quantity') {
      const askPrice = get(askPriceAtom);
      return convert(NaN /* leverage won't be used for this calculation */, askPrice)
        .quantity(state.quantity)
        .amount.toString();
    }
    return get(amountAtom);
  });
  const bidAmountAtom = atom<string>((get) => {
    const state = get(orderStateAtom);
    if (state.kind === 'quantity') {
      const bidPrice = get(bidPriceAtom);
      return convert(NaN /* leverage won't be used for this calculation */, bidPrice)
        .quantity(state.quantity)
        .amount.toString();
    }
    return get(amountAtom);
  });
  return {
    marginAtom,
    quantityAtom,
    amountAtom,
    askMarginAtom,
    bidMarginAtom,
    askQuantityAtom,
    bidQuantityAtom,
    askAmountAtom,
    bidAmountAtom,
  };
}
